import { get } from 'lodash';
import { ratioToPercentPlus } from '../../lib/format';

const formatShotLabel = ({ stats }, shotType) => {
  const attempts = get(stats.player.profile, `${shotType}_attempts`);
  const made = get(stats.player.profile, `${shotType}_made`);
  const rawPercentage = get(stats.player.profile, `${shotType}_percentage`);
  const rawRate = get(stats.player.profile, `${shotType}_rate`);
  const percentage = ratioToPercentPlus(rawPercentage);
  const rate = ratioToPercentPlus(rawRate);
  return `${made}/${attempts}\n(${percentage})\n${rate} / shots`;
};

const formatAssistLabel = ({ stats }) => {
  const { fgm, derived } = stats.player.boxscore;
  const { assisted_percentage } = stats.player.profile;
  const amount = ratioToPercentPlus(assisted_percentage);
  return `${derived.assisted} / ${fgm}\n${amount}`;
};

const formatMadeLabel = ({ stats }) => {
  const { fgm, fga } = stats.player.boxscore;
  const amount = ratioToPercentPlus(fgm / fga);
  return `${fgm}/${fga}\n${amount}`;
};

const getYAxisLabels = ({ stats }) =>
  stats.map(season => `${season.season.name}\n${season.team.name}`);

const getYMax = ({ stats }) => {
  const fga = stats.map(season => season.stats.player.boxscore.fga);
  return Math.max(...fga);
};

const getSeries = ({ stats }) => {
  const shotTypes = [
    ['atr', 'At Rim'],
    ['mid', 'Mid-Range'],
    ['three', 'From Three'],
  ];

  return shotTypes.map(([shotType, shotDescription]) => ({
    name: shotDescription,
    type: 'bar',
    stack: 'shots',
    data: stats.map(season => ({
      name: shotType,
      barMinHeight: 60,
      value: get(season.stats.player.profile, `${shotType}_attempts`),
      label: {
        show: true,
        fontSize: 11,
        position: 'insideTopLeft',
        data: formatShotLabel(season, shotType),
        formatter: params => params.data.label.data,
      },
    })),
  }));
};

const getAssistSeries = ({ stats }) => [
  {
    name: 'Assisted',
    type: 'bar',
    stack: 'assists',
    data: stats.map(season => ({
      name: 'assists',
      barMinHeight: 60,
      value: season.stats.player.boxscore.derived.assisted,
      label: {
        show: true,
        fontSize: 11,
        position: 'insideTopRight',
        data: formatAssistLabel(season),
        formatter: params => params.data.label.data,
      },
    })),
  },
];

const getMadeSeries = ({ stats }) => [
  {
    name: 'Made',
    type: 'bar',
    stack: 'made',
    data: stats.map(season => ({
      name: 'made',
      barMinHeight: 60,
      value: season.stats.player.boxscore.fgm,
      label: {
        show: true,
        fontSize: 11,
        position: 'insideTop',
        data: formatMadeLabel(season),
        formatter: params => params.data.label.data,
      },
    })),
  },
];

export const getShootingChartOptions = ({ player }) => {
  const seasons =
    player.stats &&
    player.stats
      .filter(season => season.season.name.toLowerCase() !== 'career')
      .sort((a, b) => a.season.start_year - b.season.start_year);
  const yMax = getYMax({ stats: seasons });

  return {
    title: {
      text: `#${player.jersey} - ${player.name}`,
      subtext: 'Shooting Profile',
      padding: [4, 4, 16, 4],
      textStyle: {
        lineHeight: 12,
      },
      subtextStyle: {
        fontSize: 14,
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '5%',
      containLabel: true,
    },
    barGap: '-40%',
    yAxis: {
      type: 'value',
      name: '# of Shots',
      nameLocation: 'middle',
      nameTextStyle: {
        fontStyle: 'italic',
        padding: 24,
      },
      max: yMax,
      minInterval: 100,
    },
    xAxis: {
      type: 'category',
      data: getYAxisLabels({ stats: seasons }),
      name: 'Season',
      nameLocation: 'middle',
      nameTextStyle: {
        fontStyle: 'italic',
        padding: 24,
      },
    },
    toolbox: {
      show: true,
      orient: 'horizontal',
      top: -3,
      right: 960 - 435,
      itemGap: 8,
      feature: {
        saveAsImage: {
          title: 'Save Image',
          name: `Pivot Analysis - Shooting Profile - ${player.normalized}`,
          pixelRatio: 2,
        },
        magicType: {
          type: ['bar', 'line'],
          title: {
            line: 'As Line',
            bar: 'As Bar',
          },
          option: {
            line: {
              areaStyle: {},
              label: {
                position: 'topRight',
              },
            },
          },
        },
      },
    },
    legend: {
      show: true,
      orient: 'horizontal',
      top: 'top',
      right: 20,
      selected: {
        Made: false,
      },
    },
    series: [
      ...getSeries({ stats: seasons }),
      ...getMadeSeries({ stats: seasons }),
      ...getAssistSeries({ stats: seasons }),
    ],
  };
};
